import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ProductCard } from "@/components/site/ProductCard";
import { adminApi, errorMessage } from "@/lib/api";
import { ImageManager } from "./ImageManager";
import { SpecsEditor } from "./SpecsEditor";
import { previewStatus, toForm, toPayload, validate } from "./productForm";

const input = "mt-1.5 h-11 w-full rounded-xl border border-[#E5E0D8] bg-white px-3.5 outline-none focus:border-[#C59B27] focus:ring-4 focus:ring-[#C59B27]/15";
const F = ({ label, children, className = "" }) => <label className={`block text-sm font-medium text-[#1F2421] ${className}`}>{label}{children}</label>;
const T = ({ label, k, f, set, ...rest }) => (
  <F label={label}><input value={f[k]} onChange={(e) => set({ [k]: e.target.value })} className={input} data-testid={`editor-${k.replace(/_/g, "-").replace(/([A-Z])/g, "-$1").toLowerCase()}-input`} {...rest} /></F>
);

export const ProductEditor = ({ open, product, categories, onClose, onSaved }) => {
  const [current, setCurrent] = useState(product);
  const [f, setF] = useState(toForm(product));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setCurrent(product); setF(toForm(product)); }, [product, open]);
  const set = (patch) => setF((s) => ({ ...s, ...patch }));

  const save = async () => {
    const err = validate(f);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const saved = current ? await adminApi.update(current.id, toPayload(f)) : await adminApi.create(toPayload(f));
      toast.success(current ? "Changes saved" : "Product created — now add photos");
      setCurrent(saved);
      setF(toForm(saved));
      onSaved();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const draft = { ...toPayload(f), slug: "preview", name: f.name || "Product name", availability_status: previewStatus(f), images: current?.images || [], is_demo: current?.is_demo };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[94vh] w-[calc(100vw-1rem)] max-w-5xl overflow-y-auto rounded-2xl bg-[#FDFBF7] p-4 sm:p-6" data-testid="product-editor">
        <DialogTitle className="font-display text-2xl font-bold text-[#1B4332]">{current ? "Edit product" : "Add a product"}</DialogTitle>
        <DialogDescription className="text-sm text-[#5B625D]">Changes go live only after you press Save.</DialogDescription>
        <div className="mt-4 grid gap-8 lg:grid-cols-[1fr_300px]">
          <div className="space-y-4">
            <T label="Product name *" k="name" f={f} set={set} />
            <div className="grid gap-4 sm:grid-cols-2">
              <F label="Category *">
                <Select value={f.category} onValueChange={(v) => set({ category: v })}>
                  <SelectTrigger className="mt-1.5 h-11 rounded-xl bg-white" data-testid="editor-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{(categories || []).map((c) => <SelectItem key={c.name} value={c.name} data-testid={`editor-category-${c.slug}`}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <T label="SKU (optional)" k="sku" f={f} set={set} />
              <T label="Material" k="material" f={f} set={set} placeholder="e.g. Stainless Steel" />
              <T label="Size" k="size" f={f} set={set} placeholder="e.g. 2 Litre" />
              <T label="Dimensions" k="dimensions" f={f} set={set} placeholder="e.g. 20 × 12 cm" />
              <T label="Sizes / variants (comma separated)" k="variantsText" f={f} set={set} placeholder="3 Litre, 5 Litre" />
              <T label="Brands (comma separated)" k="brandsText" f={f} set={set} placeholder="Hawkins, United" />
            </div>
            <F label="Description">
              <textarea value={f.description} onChange={(e) => set({ description: e.target.value })} rows={3} className={`${input} h-auto py-2.5`} data-testid="editor-description-input" />
            </F>
            <div><p className="mb-2 text-sm font-medium">Specifications</p><SpecsEditor specs={f.specs} onChange={(specs) => set({ specs })} /></div>
            <div className="grid gap-4 rounded-2xl border border-[#E5E0D8] bg-white p-4 sm:grid-cols-2">
              <T label="Stock quantity" k="stock_quantity" f={f} set={set} type="number" min="0" inputMode="numeric" />
              <T label="Low-stock alert at" k="low_stock_threshold" f={f} set={set} type="number" min="0" inputMode="numeric" />
              <label className="flex items-center justify-between gap-3 text-sm font-medium">Show "Contact Us" instead of stock
                <Switch checked={f.contactUs} onCheckedChange={(v) => set({ contactUs: v })} data-testid="editor-contact-us-switch" /></label>
              <label className="flex items-center justify-between gap-3 text-sm font-medium">Published on website
                <Switch checked={f.is_published} onCheckedChange={(v) => set({ is_published: v })} data-testid="editor-published-switch" /></label>
            </div>
            <div className="rounded-2xl border border-[#C59B27]/40 bg-[#FBF3DC]/60 p-4">
              <p className="flex items-center gap-2 text-sm font-semibold text-[#7A5A0B]"><Lock className="h-4 w-4" /> Private prices — never shown to customers</p>
              <div className="mt-2 grid gap-4 sm:grid-cols-2">
                <T label="Cost price (₹)" k="cost_price" f={f} set={set} type="number" min="0" step="0.01" />
                <T label="Selling price (₹)" k="selling_price" f={f} set={set} type="number" min="0" step="0.01" />
              </div>
            </div>
            <div>
              <p className="mb-2 text-sm font-medium">Photos</p>
              {current ? <ImageManager product={current} onChange={(p) => { setCurrent(p); onSaved(); }} />
                : <p className="rounded-xl border border-dashed border-[#E5E0D8] p-4 text-sm text-[#5B625D]" data-testid="editor-photos-hint">Save the product first, then add photos here.</p>}
            </div>
          </div>
          <aside className="lg:sticky lg:top-0 self-start">
            <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[#8B5A2B]">Live preview</p>
            <div data-testid="editor-preview"><ProductCard product={draft} preview /></div>
            {!f.is_published && <p className="mt-2 text-xs text-[#8A2B1E]">Hidden — customers won't see this product.</p>}
          </aside>
        </div>
        <div className="sticky bottom-0 -mx-4 -mb-4 mt-6 flex justify-end gap-2 border-t border-[#E5E0D8] bg-[#FDFBF7] p-4 sm:-mx-6 sm:-mb-6">
          <button onClick={onClose} className="h-11 rounded-full border border-[#E5E0D8] px-6 font-semibold" data-testid="editor-close-btn">Close</button>
          <button onClick={save} disabled={saving} className="h-11 rounded-full bg-[#1B4332] px-8 font-semibold text-white disabled:opacity-60" data-testid="editor-save-btn">{saving ? "Saving…" : current ? "Save changes" : "Create product"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
